"use client";

import { type FormEvent, type ReactNode, startTransition, useActionState, useEffect, useRef, useState, type RefObject } from "react";
import { ConfirmDialog, useNotify } from "@/components/toaster/app-toaster";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ActionState } from "./action-state.type";
import { useTranslations } from "next-intl";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Link } from "@/i18n/link";
import type { SimilarCustomer } from "@/modules/contact-records";
import { Check, Loader2 } from "lucide-react";
import { showFieldError } from "./field-validation";

type Props = {
  action: (previous: ActionState, data: FormData) => Promise<ActionState>;
  /** The form's own submit button. Leave out when the children bring their own buttons (several actions on one form). */
  submit?: string;
  children?: ReactNode;
  variant?: "default" | "outline" | "ghost" | "destructive";
  className?: string;
  /** For a window with a footer bar: the classes of the bar that holds `footer` (such as a Cancel button) and the submit button, which is then drawn inside it. */
  barClassName?: string;
  footer?: ReactNode;
  /** The size of the submit button (small for rows of buttons) and an icon before its words. */
  size?: "sm";
  icon?: ReactNode;
  /** For a table row: shows only the icon; `submit` becomes its name and tooltip. */
  iconOnly?: boolean;
  /** Asks first ("Are you sure?", in the site's own window) before sending. A button of the children can ask for itself with data-confirm="…". */
  confirm?: string;
  /** Keeps the submit button off (a form that is not valid yet). */
  disabled?: boolean;
};

/** A form that runs a server action, tells why it was refused (or that it worked) in a pop-up message that fades by itself, and, for a new customer that looks like existing ones, lists them in a message box and asks before going on (`duplicates`), and never loses what was typed: the fields are emptied only once the change was saved (React would otherwise reset them after every answer, a refusal included, so the form is sent by hand). */
export function ActionForm({ action, submit, children, variant = "default", className, barClassName, footer, confirm, size, icon, iconOnly, disabled }: Props) {
  const notify = useNotify();
  const te = useTranslations("admin.errors");
  const [state, formAction, pending] = useActionState(action, {});
  const { form, onSubmit, asking, approve, cancel } = useConfirmedSubmit(confirm);
  useEffect(() => { if (!state.duplicates && !(state.error && state.field && showFieldError(form.current, state.field, te(state.error)))) notify.result(state); }, [state, notify, form, te]);
  const [answered, setAnswered] = useState(state);
  const button = submit && <Button type="submit" variant={variant} size={iconOnly ? "icon-sm" : size} aria-label={iconOnly ? submit : undefined} title={iconOnly ? submit : undefined} disabled={pending || disabled} className="ms-auto">{icon}{!iconOnly && submit}</Button>;
  useEffect(() => { if (state.done && (!state.note || state.note === "folder-failed")) form.current?.reset(); }, [state, form]);
  const send = (event: FormEvent<HTMLFormElement>) => {
    onSubmit(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    const data = new FormData(event.currentTarget, (event.nativeEvent as SubmitEvent).submitter);
    startTransition(() => formAction(data));
  };
  const createAnyway = () => { setAnswered(state); const data = new FormData(form.current ?? undefined); data.set("anyway", "on"); startTransition(() => formAction(data)); };
  return (
    <form ref={form} onSubmit={send} className={cn("flex flex-wrap items-end gap-2", className)}>
      {children}
      {barClassName ? <div className={barClassName}>{footer}{button}</div> : button}
      <DuplicatesDialog files={answered === state ? undefined : state.duplicates} onCreateAnyway={createAnyway} onCancel={() => setAnswered(state)} />
      <ConfirmDialog question={asking?.question ?? null} destructive={variant === "destructive"} onConfirm={approve} onCancel={cancel} />
    </form>
  );
}

/** What a form needs to ask "Are you sure?" with the site's own window (see `ConfirmDialog`) before sending: put `form` on the form and `onSubmit` on it. A submit that has a question (the form's own, or the one on the button pressed, `data-confirm`) is held back and the question is given in `asking`;
 *  `approve` sends it after all, with the same button, and `cancel` drops it. */
function useConfirmedSubmit(confirm?: string) {
  const form = useRef<HTMLFormElement>(null);
  const approved = useRef(false);
  const [asking, setAsking] = useState<{ question: string; submitter: HTMLElement | null } | null>(null);
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const question = approved.current ? null : (submitter?.getAttribute("data-confirm") ?? confirm ?? null);
    approved.current = false;
    if (question) { event.preventDefault(); setAsking({ question, submitter }); }
  };
  const approve = () => { const pressed = asking?.submitter; setAsking(null); approved.current = true; form.current?.requestSubmit(pressed instanceof HTMLButtonElement ? pressed : undefined); };
  return { form, onSubmit, asking, approve, cancel: () => setAsking(null) };
}

/** The message box shown when a new customer looks like files that already exist: each one with its name, email and what matched (same email, same name) and a link to open it in a new tab; Cancel keeps the form as it is, "Create anyway" sends it again knowing about the others. */
function DuplicatesDialog({ files, onCreateAnyway, onCancel }: { files: SimilarCustomer[] | undefined; onCreateAnyway: () => void; onCancel: () => void }) {
  const t = useTranslations("admin.duplicates");
  return (
    <AlertDialog open={files !== undefined} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{t("title")}</AlertDialogTitle><AlertDialogDescription>{t("help")}</AlertDialogDescription></AlertDialogHeader>
        <ul className="flex flex-col gap-2">{files?.map((f) => (
          <li key={f.id} className="flex items-center justify-between gap-3 rounded-lg border p-2 text-sm">
            <span className="min-w-0"><span className="block truncate font-medium">{f.name}</span><span className="block truncate text-xs text-muted-foreground" dir="ltr">{f.email}</span><span className="text-xs text-amber-700 dark:text-amber-400">{f.matches.map((m) => t(m)).join(" · ")}</span></span>
            <Link href={`/contacts/${f.id}`} target="_blank" className="shrink-0 text-xs underline">{t("open")}</Link>
          </li>
        ))}</ul>
        <AlertDialogFooter><AlertDialogCancel>{t("cancel")}</AlertDialogCancel><AlertDialogAction onClick={onCreateAnyway}>{t("anyway")}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** A form without a Save button: every change is saved by itself (see `useAutoSave`) and a small "Saving..." then "Saved" tells so at the end. A refusal shows its reason under the field it names (else in a pop-up) and the field keeps what was typed, so the person can correct it; the server still checks everything. */
export function AutoSaveForm({ action, children, className }: { action: (previous: ActionState, data: FormData) => Promise<ActionState>; children: ReactNode; className?: string }) {
  const t = useTranslations("admin");
  const te = useTranslations("admin.errors");
  const notify = useNotify();
  const form = useRef<HTMLFormElement>(null);
  const [state, formAction, pending] = useActionState(action, {});
  useAutoSave(form, pending);
  useEffect(() => { if (state.error && !(state.field && showFieldError(form.current, state.field, te(state.error)))) notify.result(state); }, [state, notify, te]);
  const send = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); const data = new FormData(event.currentTarget); startTransition(() => formAction(data)); };
  return (
    <form ref={form} onSubmit={send} className={cn("flex flex-wrap items-end gap-2", className)}>
      {children}
      <p role="status" className="ms-auto flex h-5 basis-full items-center justify-end gap-1.5 text-xs text-muted-foreground">
        {pending ? <><Loader2 aria-hidden className="size-3.5 animate-spin" />{t("saving")}</> : state.done && <><Check aria-hidden className="size-3.5 text-primary" />{t("saved")}</>}
      </p>
    </form>
  );
}

const TYPED = "input:not([type=checkbox]):not([type=radio]):not([type=hidden]), textarea";

/** Makes a form save itself: a choice (list, tick, day) saves after 0.3 s, typing after 1 s of rest. Nothing is sent while a field is invalid or while the last save is still running; a change made meanwhile is sent right after it. */
function useAutoSave(form: RefObject<HTMLFormElement | null>, pending: boolean) {
  const waiting = useRef(false);
  const busy = useRef(pending);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const fire = useRef(() => {});
  useEffect(() => {
    const element = form.current;
    fire.current = () => { if (busy.current) waiting.current = true; else if (element?.checkValidity()) element.requestSubmit(); };
    const later = (ms: number) => { clearTimeout(timer.current); timer.current = setTimeout(() => fire.current(), ms); };
    const onChange = () => later(300);
    const onInput = (event: Event) => { if ((event.target as Element).matches(TYPED)) later(1000); };
    element?.addEventListener("change", onChange);
    element?.addEventListener("input", onInput);
    return () => { clearTimeout(timer.current); element?.removeEventListener("change", onChange); element?.removeEventListener("input", onInput); };
  }, [form]);
  useEffect(() => {
    busy.current = pending;
    if (!pending && waiting.current) { waiting.current = false; fire.current(); }
  }, [pending]);
}
